<script lang="ts">
	/**
	 * Edits the tier comparison matrix a product contributes to (§14, §4.1).
	 *
	 * A row is a group, a label and a value. **The label is the key the columns are aligned on**,
	 * so two tiers describing one capability have to spell it identically or the storefront renders
	 * two rows that each dash against the other — which looks exactly like two real, differently
	 * named features. That is the one mistake this editor exists to make hard, so it offers the
	 * labels its siblings already use rather than leaving an admin to retype them.
	 *
	 * **A blank value is a tick, and it is the common case.** `null` in the payload means "included,
	 * with no amount to state"; a value like `10` or `Unlimited` is what makes a ladder comparable
	 * at all. A row simply left out of a product renders as a dash against the columns that carry
	 * it — which is why removing a row here is how a tier says it does *not* have something, and
	 * why there is no third "excluded" state to get wrong.
	 *
	 * **A row can also be printed on the invoice**, and that is a separate decision from everything
	 * above — hence a tick per row rather than a rule. The storefront rows are the ones that
	 * *separate two tiers*; the invoice rows are the ones that make a charge legible to somebody
	 * reading the document a year later, with no access to the shop. The two sets overlap and are
	 * not the same, so a few are ticked deliberately and the rest are not: a comparison matrix
	 * transplanted whole onto an invoice turns a legal record into a sales page.
	 *
	 * `StringListEditor` was not reused: it dedupes case-insensitively and stores one string per
	 * entry, and both are wrong here. Two rows may legitimately share a label across groups, and a
	 * row is three fields — flattening it into a delimited string is a parser waiting to meet a
	 * value with a comma in it.
	 */

	import { Button, Checkbox, Input } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import type { ProductComparisonFeature } from '../AppTypes';

	/** Two-way bindable. Rows are kept in author order: the order they read in is the order set. */
	export let rows: ProductComparisonFeature[] = [];
	export let disabled: boolean = false;
	/**
	 * Every row the rest of the line already declares, in the line's own order.
	 *
	 * Two things are built from it: the datalists that suggest the existing spelling, and Copy the
	 * line's rows. Duplicates across tiers are expected — it is the raw union, deduplicated here.
	 */
	export let knownRows: ProductComparisonFeature[] = [];
	/** Unique per instance, because two of these on one screen would share their datalists. */
	export let id: string = 'comparison-features';

	const dispatcher = createEventDispatcher<{ change: { rows: ProductComparisonFeature[] } }>();

	/** One entry per distinct (group, label) in the line, in first-seen order. */
	$: lineRows = knownRows.reduce<ProductComparisonFeature[]>((unique, row) => {
		const group = row.group === null || row.group.trim().length === 0 ? null : row.group;
		if (!unique.some(seen => seen.label === row.label && seen.group === group)) {
			unique.push({ group, label: row.label, value: null });
		}
		return unique;
	}, []);

	$: knownLabels = [...new Set(lineRows.map(row => row.label))];
	$: knownGroups = [...new Set(lineRows.map(row => row.group ?? ''))].filter(group => group.length > 0);

	/** What Copy would actually add — nothing to offer once every row is already here. */
	$: missingFromLine = lineRows.filter(
		candidate => !rows.some(row => row.label.trim() === candidate.label && (row.group ?? null) === candidate.group)
	);

	function fnEmit(): void {
		rows = rows;
		dispatcher('change', { rows });
	}

	/**
	 * A new row inherits the previous row's group.
	 *
	 * Rows are written in runs — four "Limits" then three "Support" — and retyping the group on
	 * every one of them is both tedious and the likeliest way to end up with "Limits" and "limits"
	 * as two sections.
	 */
	function fnAdd(): void {
		rows = [...rows, { group: rows[rows.length - 1]?.group ?? null, label: '', value: null }];
		dispatcher('change', { rows });
	}

	/**
	 * Copy the rows the rest of the line declares, with the values left blank.
	 *
	 * Retyping a sibling's rows by hand is where the labels drift, and a label that drifts does not
	 * fail — it renders as a second row that dashes against every other tier, which looks exactly
	 * like a real feature the others lack. Copying them is the only way to be sure the columns
	 * align, so it is a button rather than a discipline.
	 *
	 * **Values are not copied.** A value belongs to the tier that declares it: carrying "Unlimited"
	 * across from the tier above would state a capability this one may not have, and the copy is
	 * meant to save typing rather than to make a claim. Blank means the tick, which the admin still
	 * has to mean — so rows land in the same state a hand-added row does, and rows already here are
	 * left exactly as they are.
	 */
	function fnCopyFromLine(): void {
		if (missingFromLine.length === 0) return;
		rows = [...rows, ...missingFromLine.map(row => ({ ...row }))];
		dispatcher('change', { rows });
	}

	function fnRemove(index: number): void {
		rows = rows.filter((_unused, position) => position !== index);
		dispatcher('change', { rows });
	}

	function fnMove(index: number, delta: number): void {
		const target = index + delta;
		if (target < 0 || target >= rows.length) return;
		const next = [...rows];
		[next[index], next[target]] = [next[target], next[index]];
		rows = next;
		dispatcher('change', { rows });
	}

	/**
	 * Blank is a tick, so it is stored as null rather than as an empty string.
	 *
	 * The cast is here rather than in the markup because a TypeScript cast inside a Svelte template
	 * does not parse.
	 */
	function fnSetValue(index: number, event: Event): void {
		const raw = (event.target as HTMLInputElement).value;
		rows[index].value = raw.trim().length === 0 ? null : raw;
		fnEmit();
	}

	/**
	 * Ticked rows are copied onto the invoice at issue, not read from here when it renders.
	 *
	 * Worth knowing while editing: unticking a row changes what the *next* invoice says and nothing
	 * about the ones already issued, which is the point — a document is a record of what the
	 * customer was told they were buying.
	 */
	function fnSetInvoice(index: number, event: Event): void {
		rows[index].includeOnInvoice = (event.target as HTMLInputElement).checked;
		fnEmit();
	}

	/**
	 * Ticks or unticks one row for the cancel dialog. Ticked rows are what a customer is shown as lost when
	 * they cancel; unticking one changes what the dialog says from now on, not anything already shown.
	 */
	function fnSetCancel(index: number, event: Event): void {
		rows[index].includeOnCancel = (event.target as HTMLInputElement).checked;
		fnEmit();
	}

	function fnSetGroup(index: number, event: Event): void {
		const raw = (event.target as HTMLInputElement).value;
		rows[index].group = raw.trim().length === 0 ? null : raw;
		fnEmit();
	}
</script>

<div class="space-y-2">
	<datalist id={`${id}-labels`}>
		{#each knownLabels as label (label)}
			<option value={label}></option>
		{/each}
	</datalist>
	<datalist id={`${id}-groups`}>
		{#each knownGroups as group (group)}
			<option value={group}></option>
		{/each}
	</datalist>

	{#if rows.length > 0}
		<div class="hidden md:grid grid-cols-[1fr_1.4fr_1fr_2.5rem_2.5rem_4rem] gap-2">
			<span class="card-label">Section</span>
			<span class="card-label">Row</span>
			<span class="card-label">This tier gives</span>
			<abbr class="card-label justify-center no-underline" title="On invoice">I</abbr>
			<abbr class="card-label justify-center no-underline" title="On cancel">C</abbr>
			<span></span>
		</div>
	{/if}

	{#each rows as row, index (index)}
		<div class="grid md:grid-cols-[1fr_1.4fr_1fr_2.5rem_2.5rem_4rem] gap-2 items-center">
			<Input
				size="sm"
				list={`${id}-groups`}
				value={row.group ?? ''}
				placeholder="Limits"
				aria-label={`Section for row ${index + 1}`}
				{disabled}
				on:input={event => fnSetGroup(index, event)}
			/>
			<Input
				size="sm"
				list={`${id}-labels`}
				bind:value={row.label}
				placeholder="Projects"
				aria-label={`Row ${index + 1} label`}
				{disabled}
				on:input={fnEmit}
			/>
			<Input
				size="sm"
				value={row.value ?? ''}
				placeholder="Blank = a tick"
				aria-label={`Value for row ${index + 1}`}
				{disabled}
				on:input={event => fnSetValue(index, event)}
			/>
			<label class="flex items-center gap-2 text-sm md:justify-center">
				<Checkbox
					checked={row.includeOnInvoice === true}
					{disabled}
					spacing=""
					class="shrink-0"
					aria-label={`Print row ${index + 1} on the invoice`}
					on:change={event => fnSetInvoice(index, event)}
				/>
				<!-- The column heading carries this from `md` up, where it would otherwise repeat
				     on every row; below that there are no columns and the box needs its own name. -->
				<span class="md:hidden">Print on the invoice</span>
			</label>
			<label class="flex items-center gap-2 text-sm md:justify-center">
				<Checkbox
					checked={row.includeOnCancel === true}
					{disabled}
					spacing=""
					class="shrink-0"
					aria-label={`Show row ${index + 1} when cancelling`}
					on:change={event => fnSetCancel(index, event)}
				/>
				<span class="md:hidden">Show when cancelling</span>
			</label>
			<div class="flex items-center gap-1">
				<Button
					color="alternative"
					size="sm"
					class="!px-2"
					disabled={disabled || index === 0}
					aria-label={`Move row ${index + 1} up`}
					on:click={() => fnMove(index, -1)}
				>
					<i class="fa-solid fa-arrow-up text-xs" />
				</Button>
				<Button
					color="alternative"
					size="sm"
					class="!px-2"
					disabled={disabled || index === rows.length - 1}
					aria-label={`Move row ${index + 1} down`}
					on:click={() => fnMove(index, 1)}
				>
					<i class="fa-solid fa-arrow-down text-xs" />
				</Button>
				<Button
					color="alternative"
					size="sm"
					class="!px-2"
					{disabled}
					aria-label={`Remove row ${index + 1}`}
					on:click={() => fnRemove(index)}
				>
					<i class="fa-solid fa-xmark text-xs" />
				</Button>
			</div>
		</div>
	{/each}

	<div class="flex flex-wrap items-center gap-2">
		<Button color="alternative" size="sm" {disabled} on:click={fnAdd}>
			<i class="fa-solid fa-plus mr-2" />Add a comparison row
		</Button>
		{#if missingFromLine.length > 0}
			<Button color="alternative" size="sm" {disabled} on:click={fnCopyFromLine}>
				<i class="fa-solid fa-copy mr-2" />Copy {missingFromLine.length}
				{missingFromLine.length === 1 ? 'row' : 'rows'} from the family
			</Button>
		{/if}
	</div>

	{#if missingFromLine.length > 0}
		<p class="text-xs text-default">
			Copying brings the row names across so the columns line up. Values stay blank: a blank
			renders as a tick, so set or clear each one for this tier.
		</p>
	{/if}

	{#if rows.length > 0}
		<p class="text-xs text-default">
			Rows ticked for the invoice are copied onto each invoice document as it is issued, under
			what was charged. Tick a few that make the charge legible on its own — invoices already
			issued keep the wording the customer received.
		</p>
	{/if}

	{#if rows.length === 0}
		<p class="text-xs text-default">
			With no rows here, this tier's column falls back to the marketing bullets, which can only
			say included or not, so tiers that differ by amount all read as a tick.
		</p>
	{/if}
</div>
